import { useState, useEffect } from "react";
import {
  Container,
  Header,
  Box,
  SpaceBetween,
  ColumnLayout,
  BarChart,
  PieChart,
  Spinner,
  StatusIndicator,
  Button,
} from "@cloudscape-design/components";
import { adminService } from "../../services/adminService";

const MODE_LABELS  = { schema: "OpsLense", knowledge_base: "DocuSense" };
const MODEL_LABELS = { claude_sonnet: "Claude Sonnet", claude_haiku: "Claude Haiku", nova: "Nova Pro" };
const label = (map, key) => map[key] || key;

const BAR_I18N = {
  filterLabel: "Filter data",
  filterPlaceholder: "Filter series",
  filterSelectedAriaLabel: "selected",
  detailPopoverDismissAriaLabel: "Dismiss",
  legendAriaLabel: "Legend",
  chartAriaRoleDescription: "bar chart",
  yTickFormatter: (v) => String(v),
};

const PIE_I18N = {
  detailsValue: "Count",
  detailsPercentage: "Percentage",
  filterLabel: "Filter data",
  filterPlaceholder: "Filter data",
  filterSelectedAriaLabel: "selected",
  detailPopoverDismissAriaLabel: "Dismiss",
  legendAriaLabel: "Legend",
  chartAriaRoleDescription: "pie chart",
  segmentAriaRoleDescription: "segment",
};

function KpiCard({ label: title, value, sub }) {
  return (
    <div>
      <Box color="text-body-secondary" fontSize="body-s">{title}</Box>
      <Box fontSize="heading-xl" fontWeight="bold" padding={{ top: "xxs" }}>{value}</Box>
      {sub && <Box color="text-body-secondary" fontSize="body-s" padding={{ top: "xxs" }}>{sub}</Box>}
    </div>
  );
}

const emptyBox = (
  <Box textAlign="center" color="text-body-secondary" padding="l">No data yet</Box>
);

export default function UsageAnalytics() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    adminService.getAnalytics()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError("Failed to load analytics data."); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <Box padding="xl" textAlign="center"><Spinner size="large" /></Box>;
  }

  if (error) {
    return (
      <Box padding="l">
        <StatusIndicator type="error">{error}</StatusIndicator>
      </Box>
    );
  }

  const helpfulRate = data.total_feedback > 0
    ? `${Math.round((data.positive_feedback / data.total_feedback) * 100)}%`
    : "N/A";

  const dayDates = data.sessions_by_day.map(d => d.date);
  const dayMax   = Math.max(...data.sessions_by_day.map(d => d.count), 1);

  const modePieData = Object.entries(data.sessions_by_mode)
    .map(([k, v]) => ({ title: label(MODE_LABELS, k), value: v }));

  const feedbackPieData = [
    { title: "Helpful",     value: data.positive_feedback, color: "#037f0c" },
    { title: "Not helpful", value: data.negative_feedback, color: "#d13212" },
  ].filter(d => d.value > 0);

  const modelKeys    = Object.keys(data.model_usage);
  const modelXDomain = modelKeys.map(k => label(MODEL_LABELS, k));

  const reasonEntries = Object.entries(data.negative_reasons)
    .sort((a, b) => b[1] - a[1]);
  const reasonXDomain = reasonEntries.map(([r]) => r);
  const reasonMax     = Math.max(...reasonEntries.map(([, v]) => v), 1);

  return (
    <SpaceBetween size="m">

      {/* KPI row */}
      <Container header={
        <Header
          variant="h2"
          actions={<Button iconName="refresh" onClick={load} loading={loading}>Refresh</Button>}
        >
          Overview
        </Header>
      }>
        <ColumnLayout columns={4} variant="text-grid">
          <KpiCard label="Total Sessions"      value={data.total_sessions} />
          <KpiCard label="Active Users (30 d)" value={data.active_users}   />
          <KpiCard label="Feedback Submitted"  value={data.total_feedback} />
          <KpiCard
            label="Helpful Rate"
            value={helpfulRate}
            sub={data.total_feedback > 0
              ? `${data.positive_feedback} helpful · ${data.negative_feedback} not helpful`
              : undefined}
          />
        </ColumnLayout>
      </Container>

      {/* Sessions per day */}
      <Container header={<Header variant="h2">Sessions per day — last 30 days</Header>}>
        <BarChart
          series={[{
            title: "Sessions",
            type:  "bar",
            data:  data.sessions_by_day.map(d => ({ x: d.date, y: d.count })),
            color: "#0972d3",
          }]}
          xDomain={dayDates}
          yDomain={[0, dayMax + 1]}
          height={200}
          ariaLabel="Sessions per day"
          i18nStrings={BAR_I18N}
          empty={emptyBox}
          noMatch={emptyBox}
          hideFilter
        />
      </Container>

      {/* Query mode + Feedback breakdown side by side */}
      <ColumnLayout columns={2}>
        <Container header={<Header variant="h2">Query Mode Split</Header>}>
          {modePieData.length === 0 ? emptyBox : (
            <PieChart
              data={modePieData}
              ariaLabel="Query mode split"
              innerMetricDescription="sessions"
              innerMetricValue={String(data.total_sessions)}
              i18nStrings={PIE_I18N}
              hideFilter
              size="medium"
              empty={emptyBox}
              noMatch={emptyBox}
            />
          )}
        </Container>

        <Container header={<Header variant="h2">Feedback Breakdown</Header>}>
          {feedbackPieData.length === 0 ? (
            <Box padding="l" textAlign="center" color="text-body-secondary">
              No feedback submitted yet
            </Box>
          ) : (
            <PieChart
              data={feedbackPieData}
              ariaLabel="Feedback breakdown"
              innerMetricDescription="responses"
              innerMetricValue={String(data.total_feedback)}
              i18nStrings={PIE_I18N}
              hideFilter
              size="medium"
              empty={emptyBox}
              noMatch={emptyBox}
            />
          )}
        </Container>
      </ColumnLayout>

      {/* Model usage */}
      {modelKeys.length > 0 && (
        <Container header={<Header variant="h2">Model Usage (by feedback)</Header>}>
          <BarChart
            series={[
              {
                title: "Helpful",
                type:  "bar",
                data:  modelKeys.map(k => ({ x: label(MODEL_LABELS, k), y: data.model_usage[k].positive || 0 })),
                color: "#037f0c",
              },
              {
                title: "Not helpful",
                type:  "bar",
                data:  modelKeys.map(k => ({ x: label(MODEL_LABELS, k), y: data.model_usage[k].negative || 0 })),
                color: "#d13212",
              },
            ]}
            xDomain={modelXDomain}
            yDomain={[0, Math.max(...modelKeys.flatMap(k => [
              data.model_usage[k].positive || 0,
              data.model_usage[k].negative || 0,
            ]), 1) + 1]}
            height={200}
            ariaLabel="Model usage"
            i18nStrings={BAR_I18N}
            empty={emptyBox}
            noMatch={emptyBox}
            hideFilter
          />
        </Container>
      )}

      {/* Not helpful reasons */}
      {reasonEntries.length > 0 && (
        <Container header={<Header variant="h2">Not Helpful — Common Reasons</Header>}>
          <BarChart
            series={[{
              title: "Count",
              type:  "bar",
              data:  reasonEntries.map(([r, v]) => ({ x: r, y: v })),
              color: "#d13212",
            }]}
            xDomain={reasonXDomain}
            yDomain={[0, reasonMax + 1]}
            height={200}
            ariaLabel="Not helpful reasons"
            i18nStrings={BAR_I18N}
            empty={emptyBox}
            noMatch={emptyBox}
            hideFilter
          />
        </Container>
      )}

    </SpaceBetween>
  );
}
